import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Card, Button, Spin, Alert, message, Tag } from "antd";
import { CreditCardOutlined } from "@ant-design/icons";
import moment from "moment";
import { useDispatch } from "react-redux";
import { API } from "../../Config/API";
import { storePaymentData } from "../../store/paymentDetails";
import Logo from "../../asset/images/logo.png";
import { DEFAULT_CURRENCY, ONLINE_PAYMENT_UNAVAILABLE_MESSAGE } from "../../utils/epaperPricing";

type SelectionPlan = {
  id: number;
  plan_name: string;
  type: string;
  duration_type: "days" | "months" | "years";
  duration_value: number;
  region?: string | null;
};

type LinkUser = {
  id: number;
  full_name: string;
  email: string | null;
  phone: string | null;
  subscriber_code: string | null;
  amount: string | null;
  current_plan_id: number | null;
  current_plan_type: string | null;
  subscription_status: string | null;
  subscription_start_date: string | null;
  subscription_end_date: string | null;
  zone: string | null;
  // Currency of the last payment.
  currency?: string | null;
};

type LinkInfo = {
  user: LinkUser;
  selection: {
    plan: SelectionPlan | null;
    subscription_start_date: string | null;
    subscription_end_date: string | null;
    amount: string | null;
    // Signed into the link with the amount (older links: AED).
    currency?: string | null;
  };
  scenario: "renewal" | "upgrade";
  expires_at: string | null;
  // The subscriber's country no longer uses the link's currency.
  online_payment_unavailable?: boolean;
};

const RenewLinkScreen = () => {
  const { token } = useParams<{ token: string }>();
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<LinkInfo | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // Set when online payment can't be used for this link (the server says so
  // up front, or PayTabs can't charge the currency).
  const [onlineUnavailable, setOnlineUnavailable] = useState(false);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `${API.BASE_URL}${API.RENEWAL_LINK_INFO}${encodeURIComponent(token)}/info`
        );
        const json = await res.json();
        if (cancelled) return;
        if (!res.ok) {
          setError(
            json?.error?.message ||
              "This link is invalid or has expired. Please request a new one."
          );
          return;
        }
        setInfo(json?.data);
        setOnlineUnavailable(json?.data?.online_payment_unavailable === true);
      } catch (err) {
        if (!cancelled) setError("Failed to load. Please try again.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handlePay = async () => {
    if (!info || !token || !info.selection.amount) return;
    setSubmitting(true);
    try {
      // The server charges the amount and currency signed into the link.
      const payload = {
        full_name: info.user.full_name,
        email: info.user.email,
        phone: info.user.phone,
        token,
      };
      const res = await fetch(`${API.BASE_URL}${API.PAYTABS_CREATE}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (data?.error?.details?.code === "ONLINE_PAYMENT_UNAVAILABLE") {
        setOnlineUnavailable(true);
        setSubmitting(false);
        return;
      }
      if (!data?.success) {
        message.error(data?.error?.message || data?.message || "Failed to start payment");
        setSubmitting(false);
        return;
      }
      dispatch(storePaymentData(data.tranRef));
      sessionStorage.setItem("paymentTransactionRef", data.tranRef);
      // Stash the link token so the post-PayTabs payment screen can call
      // /paytabs-renewal/complete on our behalf without an auth bearer.
      // Plan/dates/amount are authoritative on the server (from the token
      // claims), so we don't bother sending them again from here.
      sessionStorage.setItem(
        "paytabsRenewalContext",
        JSON.stringify({
          tran_ref: data.tranRef,
          token,
        })
      );
      window.location.href = data.paymentUrl;
    } catch (err) {
      message.error("Network error. Please try again.");
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "linear-gradient(135deg, #e8f4fb 0%, #0576b7 100%)",
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  if (error || !info) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 20,
          background: "linear-gradient(135deg, #e8f4fb 0%, #0576b7 100%)",
        }}
      >
        <Card style={{ maxWidth: 480, width: "100%" }}>
          <div style={{ textAlign: "center", marginBottom: 20 }}>
            <img src={Logo} alt="Suprabhaatham" style={{ width: 180 }} />
          </div>
          <Alert
            type="error"
            showIcon
            message="Link unavailable"
            description={error}
          />
        </Card>
      </div>
    );
  }

  const { user, selection, scenario } = info;
  const isRenewal = scenario === "renewal";
  const linkCurrency = selection.currency || DEFAULT_CURRENCY;
  const heading = isRenewal ? "Renew Subscription" : "Upgrade to Premium";
  const startMoment = selection.subscription_start_date
    ? moment(selection.subscription_start_date)
    : null;
  const endMoment = selection.subscription_end_date
    ? moment(selection.subscription_end_date)
    : null;

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #e8f4fb 0%, #0576b7 100%)",
        padding: 20,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div style={{ width: "100%", maxWidth: 560 }}>
        <Card
          style={{
            borderRadius: 20,
            boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
            border: "none",
            background: "rgba(255,255,255,0.97)",
          }}
          bodyStyle={{ padding: 32 }}
        >
          <div style={{ textAlign: "center", marginBottom: 24 }}>
            <img src={Logo} alt="Suprabhaatham" style={{ width: 180 }} />
            <h2
              style={{
                marginTop: 12,
                marginBottom: 4,
                color: "#0576b7",
                fontFamily: "NotoSansMalayalam-Bold",
              }}
            >
              {heading}
            </h2>
            <div style={{ color: "#666", fontSize: 14 }}>
              Hi {user.full_name}, complete your payment to{" "}
              {isRenewal ? "renew" : "activate"} your subscription.
            </div>
          </div>

          {/* Subscriber details */}
          <div
            style={{
              background: "#f5f9fe",
              border: "1px solid #d0e8f8",
              borderRadius: 10,
              padding: "14px 16px",
              marginBottom: 20,
            }}
          >
            <div
              style={{
                fontSize: 11,
                textTransform: "uppercase",
                letterSpacing: 0.5,
                color: "#0576b7",
                fontWeight: 600,
                marginBottom: 8,
              }}
            >
              Subscriber details
            </div>
            <div style={{ fontSize: 13, color: "#333", lineHeight: 1.8 }}>
              {user.subscriber_code && (
                <div>
                  <strong>Subscriber Code:</strong> {user.subscriber_code}
                </div>
              )}
              {user.email && (
                <div>
                  <strong>Email:</strong> {user.email}
                </div>
              )}
              {user.phone && (
                <div>
                  <strong>Phone:</strong> {user.phone}
                </div>
              )}
              {isRenewal && user.subscription_end_date && (
                <div>
                  <strong>Currently active until:</strong>{" "}
                  {moment(user.subscription_end_date).format("DD MMM YYYY")}
                  {user.subscription_status && (
                    <Tag
                      color={
                        user.subscription_status === "active"
                          ? "green"
                          : user.subscription_status === "expired"
                            ? "orange"
                            : "red"
                      }
                      style={{ marginLeft: 8 }}
                    >
                      {user.subscription_status}
                    </Tag>
                  )}
                </div>
              )}
              {isRenewal && user.amount && (
                <div>
                  <strong>Last paid:</strong> {user.currency || DEFAULT_CURRENCY} {user.amount}
                </div>
              )}
            </div>
          </div>

          {/* Locked plan summary — the admin chose this when generating the
              link, so we show it as read-only rather than letting the user
              re-pick. */}
          {selection.plan && (
            <div
              style={{
                border: "1.5px solid #0576b7",
                background: "#e8f4fb",
                borderRadius: 10,
                padding: "12px 16px",
                marginBottom: 16,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div>
                <div style={{ fontWeight: 600, color: "#222", fontSize: 15 }}>
                  {selection.plan.plan_name}
                </div>
                <div style={{ fontSize: 12, color: "#666" }}>
                  {selection.plan.duration_value}{" "}
                  {selection.plan.duration_type}
                  {selection.plan.region ? ` · ${selection.plan.region}` : ""}
                </div>
              </div>
              <div
                style={{
                  fontWeight: 700,
                  color: "#0576b7",
                  fontSize: 16,
                }}
              >
                {linkCurrency} {selection.amount}
              </div>
            </div>
          )}

          {/* Start / End / Amount summary */}
          <div
            style={{
              background: "#fff",
              border: "1px solid #e0e0e0",
              borderRadius: 10,
              padding: "12px 16px",
              marginBottom: 20,
              display: "flex",
              justifyContent: "space-around",
              textAlign: "center",
            }}
          >
            <div>
              <div style={{ fontSize: 11, color: "#888" }}>Start</div>
              <div style={{ fontWeight: 600 }}>
                {startMoment ? startMoment.format("DD MMM YYYY") : "—"}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: "#888" }}>End</div>
              <div style={{ fontWeight: 600 }}>
                {endMoment ? endMoment.format("DD MMM YYYY") : "—"}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: "#888" }}>Amount</div>
              <div style={{ fontWeight: 700, color: "#0576b7" }}>
                {selection.amount ? `${linkCurrency} ${selection.amount}` : "—"}
              </div>
            </div>
          </div>

          {onlineUnavailable && (
            <Alert
              type="warning"
              showIcon
              message={ONLINE_PAYMENT_UNAVAILABLE_MESSAGE}
              style={{ marginBottom: 16 }}
            />
          )}

          <Button
            type="primary"
            size="large"
            block
            loading={submitting}
            disabled={!selection.amount || onlineUnavailable}
            onClick={handlePay}
            icon={<CreditCardOutlined />}
            style={{
              height: 52,
              fontSize: 16,
              fontWeight: 600,
              background: "#0576b7",
              borderColor: "#0576b7",
              borderRadius: 12,
            }}
          >
            {selection.amount ? `Pay ${linkCurrency} ${selection.amount}` : "Pay Securely"}
          </Button>

          <div
            style={{
              textAlign: "center",
              marginTop: 20,
              fontSize: 12,
              color: "#888",
            }}
          >
            🔒 Secured by SSL encryption
          </div>
        </Card>
      </div>
    </div>
  );
};

export default RenewLinkScreen;
